import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { ChatPreview } from "../components/ChatPreview";
import { agentKeys } from "../queryKeys";

interface AgentData {
  id: string;
  name: string;
  display_name: string;
  welcome_message: string;
  accent_color: string;
  persona: string;
  llm_model: string;
  theme: string;
  icon?: string;
}

interface PersonalityPageProps {
  agentId: string;
  onSave?: () => Promise<void>;
}

export function PersonalityPage({ agentId, onSave }: PersonalityPageProps) {
  const queryClient = useQueryClient();
  const { data: serverAgent, isLoading: loading } = useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`).then((r) => r.agent),
  });
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setAgent(serverAgent ?? null);
  }, [serverAgent]);

  const updateField = useCallback((field: keyof AgentData, value: string) => {
    if (!agent) return;
    setAgent({ ...agent, [field]: value });
  }, [agent]);

  const saveMutation = useMutation({
    mutationFn: (payload: AgentData) =>
      apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`, {
        method: "PUT",
        body: JSON.stringify({
          welcome_message: payload.welcome_message,
          accent_color: payload.accent_color,
          persona: payload.persona,
          theme: payload.theme,
        }),
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      if (onSave) await onSave();
    },
    onError: (err: Error) => setError(err.message || "Save failed"),
  });

  const handleSave = useCallback(async () => {
    if (!agent) return;
    setError("");
    saveMutation.mutate(agent);
  }, [agent, saveMutation]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__personalitySaveHandler = handleSave;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete (window as any).__personalitySaveHandler;
      }
    };
  }, [handleSave]);

  const displayName = agent?.display_name || agent?.name || "Agent";

  if (loading)
    return (
      <div className="general-settings">
        <div className="general-settings-form-panel">
          <p style={{ color: "var(--text-muted)", padding: "4rem", textAlign: "center" }}>Loading...</p>
        </div>
        <div className="general-settings-preview-panel chat-preview-dark" />
      </div>
    );
  if (!agent && !loading)
    return (
      <div className="general-settings">
        <div className="general-settings-form-panel">
          <p style={{ color: "var(--text-muted)", padding: "4rem", textAlign: "center" }}>{error || "Agent not found"}</p>
        </div>
        <div className="general-settings-preview-panel chat-preview-dark" />
      </div>
    );

  return (
    <div className="general-settings">
      <div className="general-settings-form-panel">
        <div className="general-settings-form-inner">
          <div className="general-settings-page-header">
            <h1>Personality Settings</h1>
            <p className="page-subtitle">Customize your agent's tone, style, and visual appearance.</p>
          </div>

          <div className="general-settings-form">
            <section className="settings-section">
              <h4 className="section-header">
                <span className="material-symbols-outlined">psychology</span>
                Personality & Tone
              </h4>
              <div className="identity-fields">
                <div>
                  <label>Welcome Message</label>
                  <textarea
                    value={agent.welcome_message}
                    onChange={(e) => updateField("welcome_message", e.target.value)}
                    placeholder="Hi! How can I help you?"
                    rows={3}
                  />
                </div>
                <div>
                  <label>Persona / Tone Instructions</label>
                  <textarea
                    value={agent.persona}
                    onChange={(e) => updateField("persona", e.target.value)}
                    placeholder="e.g. You are a helpful support agent. Be concise and friendly."
                    rows={6}
                  />
                </div>
                <div>
                  <label>Accent Color</label>
                  <div className="color-row">
                    <input
                      type="color"
                      value={agent.accent_color}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                    />
                    <input
                      type="text"
                      value={agent.accent_color}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                      maxLength={7}
                      className="color-text"
                    />
                  </div>
                </div>
                <div>
                  <label>Widget Theme</label>
                  <select
                    value={agent.theme || "dark"}
                    onChange={(e) => updateField("theme", e.target.value)}
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                  <p className="form-tip">
                    Choose the color theme for your chat widget.
                  </p>
                </div>
              </div>
            </section>

            {(error || saveMutation.error) && (
              <div className="auth-error">{error || (saveMutation.error as Error)?.message}</div>
            )}
          </div>
        </div>
      </div>

      <div className={`general-settings-preview-panel ${agent.theme === "light" ? "chat-preview-light" : "chat-preview-dark"}`}>
        <ChatPreview
          agentId={agentId}
          agentName={displayName}
          model={agent.llm_model || "gpt-4o"}
          accentColor={agent.accent_color || "#137fec"}
          theme={agent.theme || "dark"}
          icon={agent.icon || "chat"}
        />
      </div>
    </div>
  );
}
